import { GoalOverviewRoadmapViewOptions } from '@griply/types';
import { Error, NotFoundPage } from 'pages/error';
import {
  GoalDetailRoutes,
  ProjectDetailRoutes,
} from 'pages/goal-detail/routes';
import { LifeAreaDetailRoutes } from 'pages/life-area-detail/routes';
import { Loading } from 'pages/loading';
import * as React from 'react';
import {
  createBrowserRouter,
  LoaderFunctionArgs,
  RouterProvider,
} from 'react-router-dom';

import { Paths } from './paths';
import { preloadLazyRoutes, whenIdle } from './preload-lazy-routes';

// Every page is its own chunk: the Electron quick entry loads /tasks/create on each open and must not parse the whole app.
const router = createBrowserRouter([
  {
    lazy: async () => ({
      Component: (await import('./app-wrapper')).AppWrapper,
    }),
    children: [
      {
        path: '*',
        element: <NotFoundPage />,
        errorElement: <Error />,
      },
      {
        path: Paths.Login,
        lazy: async () => ({ Component: (await import('pages/login')).Login }),
        errorElement: <Error />,
        loader: async () =>
          (await import('./loaders/user-loader')).loginLoader(),
      },
      {
        path: Paths.Signup,
        lazy: async () => ({
          Component: (await import('pages/signup')).Signup,
        }),
        errorElement: <Error />,
        loader: async () =>
          (await import('./loaders/user-loader')).loginLoader(),
      },
      {
        path: Paths.ForgotPassword,
        lazy: async () => ({
          Component: (await import('pages/forgot-password')).ForgotPassword,
        }),
        errorElement: <Error />,
      },
      {
        path: Paths.ResetPassword,
        lazy: async () => ({
          Component: (await import('pages/reset-password')).ResetPassword,
        }),
        errorElement: <Error />,
      },
      {
        path: Paths.CompleteSignup,
        lazy: async () => ({
          Component: (await import('pages/complete-signup')).CompleteSignup,
        }),
        errorElement: <Error />,
        loader: async () =>
          (await import('./loaders/user-loader')).completeSignupLoader(),
      },
      {
        path: Paths.VerifyEmail,
        lazy: async () => ({
          Component: (await import('pages/verify-email')).VerifyEmail,
        }),
        errorElement: <Error />,
      },
      {
        path: Paths.ConfirmEmailChange,
        lazy: async () => ({
          Component: (await import('pages/confirm-email-change'))
            .ConfirmEmailChange,
        }),
        errorElement: <Error />,
      },
      {
        path: Paths.RevertEmailChange,
        lazy: async () => ({
          Component: (await import('pages/revert-email-change'))
            .RevertEmailChange,
        }),
        errorElement: <Error />,
      },
      {
        path: Paths.DesktopConnected,
        lazy: async () => ({
          Component: (await import('pages/desktop-connected')).DesktopConnected,
        }),
        errorElement: <Error />,
      },
      {
        path: Paths.McpConnect,
        lazy: async () => ({
          Component: (await import('pages/mcp-connect')).McpConnect,
        }),
        errorElement: <Error />,
        loader: async () =>
          (await import('./loaders/user-loader')).userLoader(),
      },
      {
        path: [Paths.OpenInApp, '*'].join('/'),
        element: <Loading />,
        errorElement: <Error />,
        loader: async (args: LoaderFunctionArgs) =>
          (await import('./loaders/open-in-app-loader')).openInAppLoader(args),
      },
      {
        path: Paths.Home,
        lazy: async () => ({ Component: (await import('pages/root')).Root }),
        errorElement: <Error />,
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('pages/today')).Today,
            }),
          },
          {
            path: Paths.Inbox,
            lazy: async () => ({
              Component: (await import('pages/inbox')).Inbox,
            }),
          },
          {
            path: Paths.Upcoming,
            lazy: async () => ({
              Component: (await import('pages/upcoming')).Upcoming,
            }),
          },
          {
            path: Paths.AllTasks,
            lazy: async () => ({
              Component: (await import('pages/all-tasks-filtered')).AllTasks,
            }),
          },
          {
            path: Paths.Completed,
            lazy: async () => ({
              Component: (await import('pages/completed')).Completed,
            }),
          },
          {
            path: Paths.GoalDetail,
            lazy: async () => ({
              Component: (await import('pages/goal-detail')).GoalDetail,
            }),
            children: GoalDetailRoutes,
          },
          {
            path: Paths.Goals,
            lazy: async () => {
              const { GoalsOverviewCalendar } = await import(
                'pages/goals-overview-calendar'
              );
              return {
                element: (
                  <GoalsOverviewCalendar
                    view={GoalOverviewRoadmapViewOptions.List}
                  />
                ),
              };
            },
          },
          {
            path: Paths.Timeline,
            lazy: async () => {
              const { GoalsOverviewCalendar } = await import(
                'pages/goals-overview-calendar'
              );
              return {
                element: (
                  <GoalsOverviewCalendar
                    view={GoalOverviewRoadmapViewOptions.Timeline}
                  />
                ),
              };
            },
          },
          {
            path: Paths.Projects,
            lazy: async () => ({
              Component: (await import('pages/projects-overview'))
                .ProjectsOverview,
            }),
          },
          {
            path: Paths.ProjectDetail,
            lazy: async () => ({
              Component: (await import('pages/goal-detail')).GoalDetail,
            }),
            children: ProjectDetailRoutes,
          },
          {
            path: Paths.GoalTemplates,
            lazy: async () => ({
              Component: (await import('pages/goal-templates-overview'))
                .GoalTemplatesOverview,
            }),
          },
          {
            path: Paths.LifeAreas,
            lazy: async () => ({
              Component: (await import('pages/life-area-overview'))
                .LifeAreaOverview,
            }),
          },
          {
            path: Paths.LifeAreaDetail,
            lazy: async () => ({
              Component: (await import('pages/life-area-detail'))
                .LifeAreaDetail,
            }),
            children: LifeAreaDetailRoutes,
          },
          {
            path: Paths.Habits,
            lazy: async () => ({
              Component: (await import('pages/habits-overview')).HabitsOverview,
            }),
          },
          {
            path: Paths.Insights,
            lazy: async () => ({
              Component: (await import('pages/insights')).Insights,
            }),
          },
          {
            path: Paths.TagDetail,
            lazy: async () => ({
              Component: (await import('pages/tag-detail')).TagDetail,
            }),
          },
          {
            path: Paths.SmartListDetail,
            lazy: async () => ({
              Component: (await import('pages/smart-list-detail'))
                .SmartListDetail,
            }),
          },
          {
            path: Paths.Search,
            lazy: async () => ({
              Component: (await import('pages/search')).Search,
            }),
          },
          {
            path: Paths.Trash,
            lazy: async () => ({
              Component: (await import('pages/trash')).Trash,
            }),
          },
        ],
        loader: async () =>
          (await import('./loaders/user-loader')).userLoader(),
      },
    ],
  },
  {
    path: Paths.TaskCreate,
    lazy: async () => ({
      Component: (await import('pages/task-create')).TaskCreatePage,
    }),
  },
]);

// Once the first page shows, the rest of the opened app loads in idle time; the quick-entry route has no children, so it stays light.
const stopWaitingForFirstPage = router.subscribe(({ initialized, matches }) => {
  if (!initialized) {
    return;
  }
  stopWaitingForFirstPage();
  whenIdle(() =>
    preloadLazyRoutes(matches.slice(0, 1).map(({ route }) => route)),
  );
});

export const Routes: React.FC = () => (
  <RouterProvider router={router} fallbackElement={<Loading />} />
);
